import React, { useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useDateFormatSelect } from "store";

interface Props {
  onCancel: () => void;
  onSubmit: (data: { date: Dayjs | null; balance: string }) => void;
}

export const BalanceApproval: React.FC<Props> = ({ onCancel, onSubmit }) => {
  const { t } = useTranslation("debt");
  const dateFormat = useDateFormatSelect();

  const balanceOptions = [
    { value: "issuer", label: t("balanceApproval.issuer") },
    { value: "facility", label: t("balanceApproval.facility") },
    { value: "withdrawal", label: t("balanceApproval.withdrawal") }
  ];

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedBalance, setSelectedBalance] = useState<{ value: string; label: string } | null>(null);

  const handleSubmit = () => {
    onSubmit({ date: selectedDate, balance: selectedBalance?.value ?? "" });
  };

  return (
    <Stack sx={{ borderRadius: "8px", minWidth: "435px", padding: "20px" }}>
      <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
        <DialogTitle sx={{ fontSize: "18px", fontWeight: "400", padding: "10px 0px", color: "#071F35" }}>
          {t("balanceApproval.title")}
        </DialogTitle>
        <IconButton onClick={onCancel} size="sm" sx={{ color: "text.secondary" }}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Divider sx={{ borderColor: "#DBE8F5" }} />
      <DialogContent sx={{ padding: "20px 0px" }}>
        <Stack gap="15px">
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("balanceApproval.date")}
            value={selectedDate}
            onChange={(newValue) => setSelectedDate(newValue?.hour(12).minute(0).second(0) || null)}
            slotProps={{
              inputAdornment: {
                position: "start",
                sx: { "& .MuiSvgIcon-root": { color: "#496682" } }
              },
              textField: {
                fullWidth: true,
                placeholder: t("balanceApproval.selectDate")
              }
            }}
          />
          <Autocomplete
            fullWidth
            value={selectedBalance ?? undefined}
            disableClearable
            options={balanceOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(_, newValue) => setSelectedBalance(newValue)}
            renderInput={(params) => <TextField {...params} label={t("balanceApproval.selectBalance")} placeholder={t("balanceApproval.selectBalance")} />}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onCancel}>{t("common.cancel")}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!selectedBalance}>
          {t("balanceApproval.create")}
        </Button>
      </DialogActions>
    </Stack>
  );
};
